import { AuthService } from '@aipb/auth';
import { BaseResponse, RestRequest } from '@aipb/personal-content/shared/model';
import { SnackbarService } from '@aipb/ui/snackbar';
import { CONFIG_PERSONAL_CONTENT_API_URL, CONFIG_PERSONAL_CONTENT_ENABLED } from '@aipb/util/di';
import { isArray, isObject } from '@aipb/util/other';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PersonalContentApiError } from './personal-content-api.error';

function buildFormData(data: Record<never, never>): FormData {
  const formData = new FormData();

  for (const key of Object.keys(data)) {
    // @ts-expect-error foo
    if (isArray(data[key])) {
      // @ts-expect-error foo
      for (const i of data[key]) {
        formData.append(key + '[]', i);
      }
      // @ts-expect-error foo
    } else if (isObject(data[key])) {
      // @ts-expect-error foo
      formData.append(key, JSON.stringify(data[key]));
    } else {
      // @ts-expect-error foo
      formData.append(key, data[key]);
    }
  }

  return formData;
}

@Injectable({
  providedIn: 'root'
})
export class PersonalContentApiService {
  private readonly _baseUrl: string;
  private personalContentEnabled = inject(CONFIG_PERSONAL_CONTENT_ENABLED);
  private personalContentApiUrl = inject(CONFIG_PERSONAL_CONTENT_API_URL);

  constructor(private http: HttpClient, private auth: AuthService, private snackbar: SnackbarService) {
    this._baseUrl = this.personalContentApiUrl;
  }

  get baseUrl(): string {
    return this._baseUrl;
  }

  public post<T>(
    request: RestRequest,
    bodyData: Record<never, never> = {},
    headers: HttpHeaders = new HttpHeaders(),
    mapper: (data: BaseResponse<T>) => T = (data) => data.result
  ): Observable<T> {
    if (!this.personalContentEnabled || (this.isPrivateRequest(request) && !this.auth.isLoggedIn())) {
      return EMPTY;
    }

    const token = this.auth.token;

    bodyData = { ...bodyData, request: request, token: token };

    if (!token) {
      // @ts-expect-error foo
      delete bodyData['token'];
    }

    headers.set('Content-Type', 'application/x-www-form-urlencoded');

    const formData = buildFormData(bodyData);

    return this.http
      .post<BaseResponse<T>>(this._baseUrl, formData, {
        headers: headers
      })
      .pipe(
        map((data) => {
          if (data.error && isObject(data.error) && data.error.code !== 0 && data.error.code !== null) {
            if (data.error.code !== -26 && isDevMode()) {
              // @ts-expect-error foo
              this.snackbar.open(data.error['msg'], 'error');
            }
            throw new PersonalContentApiError({
              code: data.error['code'],
              // @ts-expect-error foo
              message: data.error['msg']
            });
          }

          return mapper(data);
        })
      );
  }

  private isPrivateRequest(request: RestRequest): boolean {
    return !this.isPublicRequest(request);
  }

  private isPublicRequest(request: RestRequest): boolean {
    const publicRequests: RestRequest[] = [
      '3ifBuildCanvasAnnotationList',
      '3ifEnhanceCanvas',
      '3ifEnhanceManifest',
      'buildCanvasAnnotationList',
      'buildManifest',
      'enhanceCanvas',
      'enhanceManifest',
      'collectionGetNameById',
      'userListCollections3if',
      'userListCollections3if_inner',
      'initServices',
      'infoAll',
      'infoDisseminationTemplatesList',
      'infoDisseminationLicensesList',
      'infoNibblesSupportedTypes',
      'infoServiceListByType',
      'infoServiceDetail',
      'nibbleGet'
    ];

    return publicRequests.includes(request);
  }
}
